import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listNotifications, markNotificationsRead } from "../services/storage";

const CHECK_EVERY = 30 * 1000;

// "5 min ago", "yesterday".
const ago = (iso, language) => {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const format = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  for (const [unit, size] of [
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ]) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, "second");
};

const ICONS = { yes: "✓", no: "✕", maybe: "?" };

// The host's notifications: a bell with the number of replies they haven't
// seen, checked every 30 seconds and whenever they come back to the tab.
// Opening it lists who replied to which invitation (the new ones stay
// highlighted while the list is open) and marks them as seen; picking one
// opens that invitation's guest list with the reply highlighted.
export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [fresh, setFresh] = useState(() => new Set());
  const box = useRef(null);

  const load = useCallback(
    () =>
      listNotifications()
        .then((latest) => {
          if (latest) setData(latest);
          return latest;
        })
        .catch(() => null),
    []
  );

  useEffect(() => {
    load();
    const refresh = () => document.visibilityState === "visible" && load();
    const timer = setInterval(refresh, CHECK_EVERY);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    const onClick = (event) => !box.current?.contains(event.target) && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    const latest = (await load()) || data;
    const unseen = latest.items.filter((item) => !item.read).map((item) => item.id);
    setFresh(new Set(unseen));
    if (unseen.length) {
      setData((current) => ({
        unread: Math.max(0, current.unread - unseen.length),
        items: current.items.map((item) => ({ ...item, read: true })),
      }));
      markNotificationsRead(unseen).catch(() => {});
    }
  };

  const openReply = (item) => {
    setOpen(false);
    navigate(`/events/${item.eventId}?reply=${item.rsvpId}`);
  };

  const says = (item) => {
    const answer =
      item.status === "yes" && item.party > 1
        ? t("notifications.yesParty", { count: item.party })
        : t(`notifications.${item.status}`, { defaultValue: t("notifications.replied") });
    return item.kind === "rsvp.changed" ? t("notifications.changed", { answer }) : answer;
  };

  const unread = data.unread;
  return (
    <div className="bell" ref={box}>
      <button
        type="button"
        className={`bell__button${unread ? " has-unread" : ""}`}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread ? t("notifications.labelUnread", { count: unread }) : t("notifications.title")}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span className="bell__badge">{unread > 99 ? "99+" : unread}</span>}
      </button>

      {open && (
        <div className="bell__panel" role="dialog" aria-label={t("notifications.title")}>
          <header className="bell__head">
            <strong>{t("notifications.title")}</strong>
            {fresh.size > 0 && <span className="bell__new">{t("notifications.newCount", { count: fresh.size })}</span>}
          </header>
          {data.items.length === 0 ? (
            <div className="bell__empty">
              <span aria-hidden="true">🔔</span>
              <p>{t("notifications.empty")}</p>
            </div>
          ) : (
            <ul className="bell__list">
              {data.items.map((item) => (
                <li key={item.id}>
                  <button type="button" className={`notice-item${fresh.has(item.id) ? " is-new" : ""}`} onClick={() => openReply(item)}>
                    <span className={`notice-item__icon notice-item__icon--${item.status}`} aria-hidden="true">
                      {ICONS[item.status] || "•"}
                    </span>
                    <span className="notice-item__body">
                      <span className="notice-item__text">
                        <strong>{item.guest || t("notifications.someone")}</strong> {says(item)}
                      </span>
                      <span className="notice-item__meta">
                        {item.eventTitle || t("dashboard.untitled")} · <time dateTime={item.createdAt}>{ago(item.createdAt, language)}</time>
                      </span>
                    </span>
                    {fresh.has(item.id) && <span className="notice-item__dot" aria-label={t("notifications.unseen")} />}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <footer className="bell__foot">
            <Link to="/dashboard" onClick={() => setOpen(false)}>
              {t("notifications.allInvitations")} →
            </Link>
          </footer>
        </div>
      )}
    </div>
  );
}
